import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Gift, Users, Link as LinkIcon, Copy, CheckCircle } from 'lucide-react';
import apiService from '../services/api';

export default function ReferralModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchReferralInfo();
    }
  }, [isOpen]);

  const fetchReferralInfo = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await apiService.get('/users/referral-info');
      
      const realDomain = window.location.origin;
      data.data.referral_url = `${realDomain}/register?ref=${data.data.referral_code}`;
      
      setReferralData(data.data);
    } catch (e) {
      setError(e.message || t('auth.tech_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (referralData?.referral_url) {
      navigator.clipboard.writeText(referralData.referral_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in">
      <div className="bg-slate-900 border border-white/10 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="relative p-6 border-b border-white/5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/10 rounded-lg text-indigo-400">
                <Gift size={20} />
              </div>
              <h2 className="text-xl font-bold text-white tracking-tight">{t('referral.title')}</h2>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-white/5 rounded-full text-slate-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-8">
          {error && (
            <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-rose-400 text-sm flex items-center gap-3">
              <span className="text-lg">⚠️</span>
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
              <p className="text-slate-400 text-sm font-medium">{t('referral.loading')}</p>
            </div>
          ) : referralData ? (
            <div className="space-y-8">
              {/* Promo Section */}
              <div className="text-center space-y-3">
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-full flex items-center justify-center mx-auto ring-4 ring-white/5 shadow-inner">
                  <Users size={32} className="text-indigo-400" />
                </div>
                <h3 className="text-2xl font-black text-white tracking-tighter">{t('referral.invite_friends')}</h3>
                <p className="text-slate-400 text-sm leading-relaxed max-w-[280px] mx-auto">
                  {t('referral.invite_desc')}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-950/50 border border-white/5 p-4 rounded-2xl text-center group hover:border-indigo-500/30 transition-colors">
                  <div className="text-3xl font-black text-indigo-400 group-hover:scale-110 transition-transform">
                    {referralData.direct_referrals_count}
                  </div>
                  <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">
                    {t('referral.direct_referrals')}
                  </div>
                </div>
                <div className="bg-slate-950/50 border border-white/5 p-4 rounded-2xl text-center group hover:border-emerald-500/30 transition-colors">
                  <div className="text-3xl font-black text-emerald-400 group-hover:scale-110 transition-transform font-mono uppercase">
                    {referralData.referral_code}
                  </div>
                  <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">
                    {t('referral.current_code')}
                  </div>
                </div>
              </div>

              {/* Referral Link */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                  {t('referral.unique_link')}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1 group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-indigo-400/50">
                      <LinkIcon size={14} />
                    </div>
                    <input 
                      type="text" 
                      value={referralData.referral_url} 
                      readOnly
                      className="w-full bg-slate-950 border border-white/10 group-hover:border-indigo-500/30 rounded-2xl py-4 pl-10 pr-4 text-xs font-mono text-indigo-300 outline-none transition-all focus:ring-2 ring-indigo-500/20"
                    />
                  </div>
                  <button 
                    onClick={handleCopy}
                    className={`px-6 rounded-2xl flex items-center justify-center gap-2 font-bold text-sm transition-all shadow-xl ${
                      copied 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-500/20'
                    }`}
                  >
                    {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                    <span>{copied ? t('referral.copied') : t('referral.copy')}</span>
                  </button>
                </div>
              </div>
              
              {referralData.referrer && (
                <div className="pt-4 border-t border-white/5 flex items-center justify-center gap-2 text-xs">
                  <span className="text-slate-500">{t('referral.referred_by')}</span>
                  <span className="px-2 py-0.5 bg-white/5 rounded text-slate-200 font-bold uppercase tracking-wider">
                    {referralData.referrer.username}
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
